import type { ProductConfig, ProductName } from '@adminhub/product-config';
import { fromProductConfig } from '@adminhub/product-config';
import type { SITE_INPUT_MODE } from '@adminhub/types';

import type { AddProductSitelessScreen } from './analytics-event-data';
import { productsOverviewScreen } from './analytics-event-data';
import type { FeatureEvents, OperationalEvent, ScreenEvent, TrackEvent, UIEvent } from '../new-analytics-types';
import { Action, ActionSubject, OperationalAction, OperationalSubject, TrackAction, TrackActionSubject } from '../new-analytics-types';

type ScreenKeys =
  | 'addProductSitelessSelectScreenEvent'
  | 'addProductSitelessAddDetailsScreenEvent'
  | 'addProductSitelessProvisioningScreenEvent'
  | 'addProductSitelessSetupScreenEvent'
  | 'addProductSitelessLoomDataSelectionScreenEvent'
  | 'selectSiteModalScreenEvent'
  | 'addProductSitelessProvisioningLoadingAndDoneScreenEvent'
  | 'addProductSitelessErrorScreenEvent';

type UIEventKeys =
  | 'addProductClickEvent'
  | 'discoverMoreClickEvent'
  | 'drawerCloseClickEvent'
  | 'learnMoreClickEvent'
  | 'backClickEvent'
  | 'createNewSiteInfoHoverEvent'
  | 'createNewSiteLearnMoreClickEvent'
  | 'selectSiteInfoHoverEvent'
  | 'agreeAndStartNowClickEvent'
  | 'selectClickEvent'
  | 'selectStandardClickEvent'
  | 'selectEnterpriseClickEvent'
  | 'addProductLearnMoreClickEvent'
  | 'termsOfServiceClickEvent'
  | 'privacyPolicyClickEvent'
  | 'manageUsersClickEvent'
  | 'goToProductClickEvent'
  | 'discoverNewProductsClickEvent'
  | 'enterpriseQuotingBannerClickEvent'
  | 'currentPricingClickEvent'
  | 'consentCheckboxToggleEvent'
  | 'goToAppClickEvent'
  | 'seeAppsListClickEvent'
  | 'backClickEvent'
  | 'contactSupportClickEvent';

type TrackEventKeys =
  | 'createdInstance'
  | 'productActivationCompleted'
  | 'rovoGAStarted'
  | 'rovoSiteStarted'
  | 'eligibilityChecked'
  | 'provisionStandaloneCollectionApp';

export const addProductSitelessSelectScreen: AddProductSitelessScreen = 'addProductSitelessSelectScreen';
export const addProductSitelessAddDetailsScreen: AddProductSitelessScreen = 'addProductSitelessAddDetailsScreen';
export const addProductSitelessProvisioningScreen: AddProductSitelessScreen = 'addProductSitelessProvisioningScreen';
export const addProductSitelessSetupScreen: AddProductSitelessScreen = 'addProductSitelessSetupScreen';
export const addProductSitelessSuccessScreen: AddProductSitelessScreen = 'addProductSitelessSuccessScreen';
export const addProductSitelessLoomDataSelectionScreen: AddProductSitelessScreen = 'addProductSitelessLoomDataSelectionScreen';
export const selectSiteModalScreen: AddProductSitelessScreen = 'selectSiteModalScreen';
export const addProductSitelessProvisioningLoadingAndDoneScreen: AddProductSitelessScreen =
  'addProductSitelessProvisioningLoadingAndDoneScreen';
export const addProductSitelessErrorScreen: AddProductSitelessScreen = 'addProductSitelessErrorScreen';

export const sitelessAddProductEvents: FeatureEvents<ScreenKeys, UIEventKeys, TrackEventKeys, string> = {
  confluenceSpec: [
    'https://hello.atlassian.net/wiki/spaces/CLOUDADMIN/pages/2288539476/Analytics+Spec+M2+Add+product+flow',
    'https://hello.atlassian.net/wiki/spaces/~7120208cbfeee96f8441dea1f49b9ee065b281/pages/5987936350/Add+app+to+TWC+Enterprise+Analytics+Spec',
  ],
  screen: {
    addProductSitelessSelectScreenEvent: (attributes: { emauProducts: string[] }): ScreenEvent => ({
      data: {
        name: addProductSitelessSelectScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/49818',
    }),
    addProductSitelessAddDetailsScreenEvent: (attributes: {
      availableSites: number;
      isEnterpriseEnabled: boolean;
      product: ProductName;
      collection?: string;
      shouldShowCrossFlowAsDefault?: boolean;
      hasPaymentDetails?: boolean;
      targetEdition?: string;
    }): ScreenEvent => ({
      data: {
        name: addProductSitelessAddDetailsScreen,
        attributes: {
          ...attributes,
          emauProduct: fromProductConfig({ name: attributes.product }).emauKey,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/49819',
    }),

    addProductSitelessProvisioningScreenEvent: (attributes: { product: ProductName }): ScreenEvent => ({
      data: {
        name: addProductSitelessProvisioningScreen,
        attributes: {
          ...attributes,
          emauProduct: fromProductConfig({ name: attributes.product }).emauKey,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/49820',
    }),
    addProductSitelessSetupScreenEvent: (attributes: { cloudId: string; product: ProductName; isEnterprise: boolean }): ScreenEvent => ({
      data: {
        name: addProductSitelessSetupScreen,
        attributes: {
          ...attributes,
          emauProduct: fromProductConfig({ name: attributes.product }).emauKey,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/51707',
    }),
    selectSiteModalScreenEvent: (attributes: { existingSites: number }): ScreenEvent => ({
      data: {
        name: selectSiteModalScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/52876',
    }),
    addProductSitelessLoomDataSelectionScreenEvent: (attributes: { product: ProductName }): ScreenEvent => ({
      data: {
        name: addProductSitelessLoomDataSelectionScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/87039',
    }),
    addProductSitelessProvisioningLoadingAndDoneScreenEvent: (attributes: { app: string; collection: string }): ScreenEvent => ({
      data: {
        name: addProductSitelessProvisioningLoadingAndDoneScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91698',
    }),
    addProductSitelessErrorScreenEvent: (attributes: { app: string }): ScreenEvent => ({
      data: {
        name: addProductSitelessErrorScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91697',
    }),
  },
  ui: {
    addProductClickEvent: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'addProduct',
        actionSubject: ActionSubject.Button,
        source: productsOverviewScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/27990',
    }),
    discoverMoreClickEvent: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'discoverMore',
        actionSubject: ActionSubject.Link,
        source: addProductSitelessSelectScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/49817',
    }),
    drawerCloseClickEvent: (
      attributes: { product?: ProductName; isEnterprise?: boolean },
      data: { source: AddProductSitelessScreen },
    ): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'drawerClose',
        source: data.source,
        attributes: {
          ...attributes,
          ...(attributes.product ? { emauProduct: fromProductConfig({ name: attributes.product }).emauKey } : {}),
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/47441',
    }),
    learnMoreClickEvent: (attributes: { product: ProductName }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'learnMore',
        actionSubject: ActionSubject.Link,
        source: addProductSitelessSelectScreen,
        attributes: {
          ...attributes,
          emauProduct: fromProductConfig({ name: attributes.product }).emauKey,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/16182',
    }),
    selectClickEvent: (attributes: { isEnterprise: boolean; product: ProductName }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'select',
        actionSubject: ActionSubject.Button,
        source: addProductSitelessSelectScreen,
        attributes: {
          ...attributes,
          emauProduct: fromProductConfig({ name: attributes.product }).emauKey,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/27994',
    }),
    backClickEvent: (
      attributes: {
        product: ProductName | null;
        collection?: string;
        isEnterprise: boolean;
        targetEdition?: string;
        hasPaymentDetails?: boolean;
        shouldShowCrossFlowAsDefault?: boolean;
      },
      data: { source: AddProductSitelessScreen },
    ): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'back',
        actionSubject: ActionSubject.Button,
        source: data.source,
        attributes: {
          ...attributes,
          ...(attributes.product ? { emauProduct: fromProductConfig({ name: attributes.product }).emauKey } : {}),
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/28051',
    }),
    createNewSiteInfoHoverEvent: (attributes: { product: ProductName; isEnterprise: boolean }): UIEvent => ({
      data: {
        action: Action.Hovered,
        actionSubjectId: 'createNewSiteInfo',
        actionSubject: ActionSubject.Icon,
        source: addProductSitelessAddDetailsScreen,
        attributes: {
          ...attributes,
          emauProduct: fromProductConfig({ name: attributes.product }).emauKey,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/50282',
    }),
    createNewSiteLearnMoreClickEvent: (attributes: { product: ProductName; isEnterprise: boolean }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'createSiteLearnMore',
        actionSubject: ActionSubject.Link,
        source: addProductSitelessAddDetailsScreen,
        attributes: {
          ...attributes,
          emauProduct: fromProductConfig({ name: attributes.product }).emauKey,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/50283',
    }),
    selectSiteInfoHoverEvent: (attributes: { product: ProductName; isEnterprise: boolean }): UIEvent => ({
      data: {
        action: Action.Hovered,
        actionSubjectId: 'selectSiteInfo',
        actionSubject: ActionSubject.Icon,
        source: addProductSitelessAddDetailsScreen,
        attributes: {
          ...attributes,
          emauProduct: fromProductConfig({ name: attributes.product }).emauKey,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/54304',
    }),
    agreeAndStartNowClickEvent: (
      attributes: {
        product: ProductName;
        collection?: string;
        isEnterprise: boolean;
        targetEdition?: string;
        hasPaymentDetails?: boolean;
        shouldShowCrossFlowAsDefault?: boolean;
        siteInputMode?: SITE_INPUT_MODE;
      },
      data: { source: AddProductSitelessScreen },
    ): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'agreeAndStartNow',
        actionSubject: ActionSubject.Button,
        source: data.source,
        attributes: {
          ...attributes,
          emauProduct: fromProductConfig({ name: attributes.product }).emauKey,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/50213',
    }),
    selectEnterpriseClickEvent: (attributes: { product: ProductName }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'selectEnterprise',
        actionSubject: ActionSubject.Button,
        source: addProductSitelessAddDetailsScreen,
        attributes: {
          ...attributes,
          emauProduct: fromProductConfig({ name: attributes.product }).emauKey,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/38768',
    }),
    selectStandardClickEvent: (attributes: { product: ProductName }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'selectStandard',
        actionSubject: ActionSubject.Button,
        source: addProductSitelessAddDetailsScreen,
        attributes: {
          ...attributes,
          emauProduct: fromProductConfig({ name: attributes.product }).emauKey,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/38767',
    }),
    addProductLearnMoreClickEvent: (attributes: { product: ProductName; isEnterprise: boolean }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'addProductLearnMore',
        actionSubject: ActionSubject.Link,
        source: addProductSitelessAddDetailsScreen,
        attributes: {
          ...attributes,
          emauProduct: fromProductConfig({ name: attributes.product }).emauKey,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/50018',
    }),
    termsOfServiceClickEvent: (
      attributes: { product: ProductName; isEnterprise: boolean },
      data: { source: AddProductSitelessScreen },
    ): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'termsOfService',
        actionSubject: ActionSubject.Link,
        source: data.source,
        attributes: {
          ...attributes,
          emauProduct: fromProductConfig({ name: attributes.product }).emauKey,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/60241',
    }),
    privacyPolicyClickEvent: (
      attributes: { product: ProductName; isEnterprise: boolean },
      data: { source: AddProductSitelessScreen },
    ): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'privacyPolicy',
        actionSubject: ActionSubject.Link,
        source: data.source,
        attributes: {
          ...attributes,
          emauProduct: fromProductConfig({ name: attributes.product }).emauKey,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/50024',
    }),
    manageUsersClickEvent: (attributes: { cloudId: string; product: ProductName; isEnterprise: boolean }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'manageUsers',
        actionSubject: ActionSubject.Button,
        source: addProductSitelessSetupScreen,
        attributes: {
          ...attributes,
          emauProduct: fromProductConfig({ name: attributes.product }).emauKey,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/20211',
    }),
    goToProductClickEvent: (attributes: {
      cloudId: string;
      product: ProductName;
      isEnterprise: boolean;
      workspaceAri: string;
    }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'goToProduct',
        actionSubject: ActionSubject.Button,
        source: addProductSitelessSetupScreen,
        attributes: {
          ...attributes,
          emauProduct: fromProductConfig({ name: attributes.product }).emauKey,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/51708',
    }),
    discoverNewProductsClickEvent: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'discoverNewProduct',
        actionSubject: ActionSubject.Button,
        source: addProductSitelessSelectScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/52877',
    }),
    enterpriseQuotingBannerClickEvent: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'enterpriseQuotingBannerCard',
        actionSubject: ActionSubject.Card,
        source: addProductSitelessSelectScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/59169',
    }),
    currentPricingClickEvent: (attributes: { targetEdition: string; targetProduct: ProductName; hasPaymentDetails: boolean }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'currentPricing',
        actionSubject: ActionSubject.Link,
        source: addProductSitelessAddDetailsScreen,
        product: 'admin',
        attributes: {
          ...attributes,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/84684',
    }),
    consentCheckboxToggleEvent: (attributes: {
      targetEdition: string;
      targetProduct: ProductName;
      hasPaymentDetails: boolean;
      isCrossFlowAsDefaultConsentChecked: boolean;
    }): UIEvent => ({
      data: {
        action: Action.Toggled,
        actionSubjectId: 'crossFlowAsDefaultConsent',
        actionSubject: ActionSubject.Checkbox,
        source: addProductSitelessAddDetailsScreen,
        product: 'admin',
        attributes: {
          ...attributes,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/84685',
    }),
    goToAppClickEvent: (attributes: { app: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'goToApp',
        actionSubject: ActionSubject.Link,
        source: addProductSitelessProvisioningLoadingAndDoneScreen,
        product: 'admin',
        attributes: {
          ...attributes,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91679',
    }),
    seeAppsListClickEvent: (attributes: { app: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'seeAppsList',
        actionSubject: ActionSubject.Link,
        source: addProductSitelessProvisioningLoadingAndDoneScreen,
        product: 'admin',
        attributes: {
          ...attributes,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86037',
    }),
    contactSupportClickEvent: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'contactSupport',
        actionSubject: ActionSubject.Button,
        source: addProductSitelessErrorScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/72146',
    }),
  },
  track: {
    createdInstance: (attributes: { isEnterprise: boolean; product: ProductName; cloudId?: string }): TrackEvent => ({
      data: {
        action: TrackAction.Created,
        actionSubject: TrackActionSubject.Instance,
        source: addProductSitelessAddDetailsScreen,
        attributes: {
          ...attributes,
          emauProduct: fromProductConfig({ name: attributes.product }).emauKey,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/34968',
    }),
    productActivationCompleted: (attributes: { isEnterprise: boolean; product: ProductName; cloudId: string }): TrackEvent => ({
      data: {
        action: TrackAction.Completed,
        actionSubject: TrackActionSubject.ProductActivation,
        source: addProductSitelessSetupScreen,
        attributes: {
          ...attributes,
          emauProduct: fromProductConfig({ name: attributes.product }).emauKey,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/52439',
    }),
    rovoGAStarted: (attributes: { isSuccess: boolean; timestamp: number; source: string }): TrackEvent => ({
      data: {
        action: TrackAction.Started,
        actionSubject: TrackActionSubject.RovoGA,
        actionSubjectId: 'rovoGAStarted',
        source: attributes.source,
        attributes: {
          isSuccess: attributes.isSuccess,
          timestamp: attributes.timestamp,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/71985',
    }),
    rovoSiteStarted: (attributes: { isSuccess: boolean; timestamp: number; source: string }): TrackEvent => ({
      data: {
        action: TrackAction.Started,
        actionSubject: TrackActionSubject.RovoSite,
        actionSubjectId: 'rovoSite',
        source: attributes.source,
        attributes: {
          isSuccess: attributes.isSuccess,
          timestamp: attributes.timestamp,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/71986',
    }),
    eligibilityChecked: (attributes: {
      source: string;
      flagKey: string;
      flagCohort: string;
      ineligibilityReasons: string[] | undefined;
      targetProduct: ProductConfig['productName'];
    }): TrackEvent => ({
      data: {
        source: attributes.source,
        actionSubject: TrackActionSubject.Eligibility,
        action: TrackAction.Checked,
        attributes: {
          flagKey: attributes.flagKey,
          flagCohort: attributes.flagCohort,
          ineligibilityReasons: attributes.ineligibilityReasons,
          targetProduct: attributes.targetProduct,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/71036',
    }),
    provisionStandaloneCollectionApp: (attributes: { app: string; collection: string; isEnterprise: boolean }): TrackEvent => ({
      data: {
        action: TrackAction.Completed,
        actionSubject: TrackActionSubject.ProvisionStandaloneCollectionApp,
        source: addProductSitelessProvisioningLoadingAndDoneScreen,
        attributes: {
          app: attributes.app,
          collection: attributes.collection,
          isEnterprise: attributes.isEnterprise,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91827',
    }),
  },
  operational: {
    updateUserConsentFailed: (attributes: {
      source: string;
      hasPaymentDetails: boolean;
      targetProduct: ProductConfig['productName'];
      errorMessage: string;
    }): OperationalEvent => ({
      data: {
        source: attributes.source,
        actionSubject: OperationalSubject.UpdateUserConsent,
        action: OperationalAction.Failed,
        attributes: {
          hasPaymentDetails: attributes.hasPaymentDetails,
          targetProduct: attributes.targetProduct,
          errorMessage: attributes.errorMessage,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85320',
    }),
  },
};
